import { Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FinanceInventoryLink } from "../FinanceInventoryLink";
import { InternalTypographyLink } from "../InternalLink";
import { CustomTable } from "../Tables/CustomTable";
import { RentCollectionItem } from "~/types/finance";
import {
  IN_PROGRESS_MOBILE_CONFIG,
  NEVER_PAID_MOBILE_CONFIG,
} from "./constants";
import {
  formatRentAmount,
  getTenantDisplayName,
  renderInProgressStatus,
} from "./utils";

const buildNeverPaidColumns = (
  t: TFunction<"translation">,
): ColumnsType<RentCollectionItem> => [
  {
    title: t("Tenant"),
    key: "tenant",
    width: 180,
    render: (_, record) => {
      const name = getTenantDisplayName(record, t);
      if (record.tenantResolved === false || !record.tenant?.id) {
        return name;
      }
      return (
        <InternalTypographyLink to={`/tenant/${record.tenant.id}`}>
          {name}
        </InternalTypographyLink>
      );
    },
  },
  {
    title: `${t("Unit")} / ${t("Parcel")}`,
    key: "unit",
    width: 180,
    render: (_, record) => (
      <FinanceInventoryLink
        entityType={record.inventoryEntityType}
        entityID={record.unit?.id}
        name={record.unit?.name}
      />
    ),
  },
  {
    title: t("Property"),
    key: "property",
    width: 180,
    render: (_, record) =>
      record.property?.id ? (
        <InternalTypographyLink to={`/property/${record.property.id}`}>
          {record.property.name || record.property.id}
        </InternalTypographyLink>
      ) : (
        t("Unavailable")
      ),
  },
  {
    title: t("Expected"),
    key: "expectedRent",
    dataIndex: "expectedRent",
    width: 120,
    render: (amount: number) => formatRentAmount(amount),
  },
  {
    title: t("Paid"),
    key: "paidRent",
    dataIndex: "paidRent",
    width: 120,
    render: (amount: number) => formatRentAmount(amount),
  },
  {
    title: t("Balance"),
    key: "balanceDue",
    dataIndex: "balanceDue",
    width: 120,
    render: (amount: number) => formatRentAmount(amount),
  },
];

const buildInProgressColumns = (
  t: TFunction<"translation">,
): ColumnsType<RentCollectionItem> => [
  ...buildNeverPaidColumns(t),
  {
    title: t("Status"),
    key: "inProgressReason",
    width: 140,
    render: (_, record) => renderInProgressStatus(t, record),
  },
];

export type RentDueTableVariant = "neverPaid" | "inProgress";

export type RentDueTableProps = {
  items: RentCollectionItem[];
  variant: RentDueTableVariant;
  rowKeyPrefix: string;
  emptyText: string;
};

export const RentDueTable = ({
  items,
  variant,
  rowKeyPrefix,
  emptyText,
}: RentDueTableProps) => {
  const { t } = useTranslation();
  const columns =
    variant === "neverPaid"
      ? buildNeverPaidColumns(t)
      : buildInProgressColumns(t);
  const mobileCardConfig =
    variant === "neverPaid"
      ? NEVER_PAID_MOBILE_CONFIG
      : IN_PROGRESS_MOBILE_CONFIG;

  if (items.length === 0) {
    return (
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        {emptyText}
      </Typography.Paragraph>
    );
  }

  return (
    <CustomTable
      style={{ marginTop: 0 }}
      tableHeaders={null}
      mobileCardConfig={mobileCardConfig}
      mobileDefaultSort={{
        dataIndex: "balanceDue",
        order: "descend",
      }}
      tableProps={{
        bordered: true,
        scroll: { x: "max-content" },
        rowKey: (record) => `${record.leaseID}-${rowKeyPrefix}`,
        dataSource: items,
        columns,
        pagination: items.length > 10 ? { pageSize: 10 } : false,
        locale: { emptyText },
      }}
    />
  );
};
